/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "App";
import AppErrorBoundary from "components/AppErrorBoundary";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "context/AuthContext";
import { CurrencyProvider } from "context/CurrencyContext";
import { CartProvider } from "context/CartContext";
import { StorefrontTenantProvider } from "context/StorefrontTenantContext";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import { bootstrapStorefront } from "lib/storefront";
import { bootstrapStorefrontPreview } from "lib/storefrontPreview";

// Material Dashboard 2 React Context Provider
import { MaterialUIControllerProvider } from "context";

const container = document.getElementById("app");
const root = createRoot(container);

const queryClient = new QueryClient();

// Ensure domain-based company context is set before any API calls.
bootstrapStorefront();
bootstrapStorefrontPreview();

if (typeof window !== "undefined") {
  window.addEventListener("active-company-changed", () => {
    queryClient.clear();
  });
  window.addEventListener("storage", (event) => {
    if (event.key === "activeCompany") queryClient.clear();
  });
}

root.render(
  <BrowserRouter>
    <MaterialUIControllerProvider>
      <QueryClientProvider client={queryClient}>
        <StorefrontTenantProvider>
          <AuthProvider>
            <CurrencyProvider>
              <CartProvider>
                <AppErrorBoundary>
                  <App />
                </AppErrorBoundary>
              </CartProvider>
            </CurrencyProvider>
          </AuthProvider>
        </StorefrontTenantProvider>
      </QueryClientProvider>
    </MaterialUIControllerProvider>
  </BrowserRouter>
);

// Register a network-first service worker for installability. Workflows remain online-required.
serviceWorkerRegistration.register();
